import { showWebPaymentButtons } from "./web-payments";

/**
 * Stripe Payment Links integration.
 *
 * REDIRECT URL CONFIGURATION (set in Stripe Dashboard, per link):
 *   After payment → Don't show confirmation page → Redirect to:
 *     createSchoolSubscription      → https://<your-domain>/payment-success?plan=create-school
 *     joinSchoolSubscription        → https://<your-domain>/payment-success?plan=join-school
 *     adultBibleStudyWeekly         → https://<your-domain>/payment-success?plan=adult-bible-study
 *     stargateControlTowerLifetime  → https://<your-domain>/payment-success?plan=stargate-control-tower
 *
 * IMPORTANT: The /payment-success page is a LANDING page only — it does NOT
 * verify that payment actually cleared. A user could visit the URL directly.
 *
 * TODO (Phase 2 — automatic entitlement activation):
 *   Add a Stripe webhook endpoint (e.g. POST /api/stripe/webhook) that
 *   verifies the signed event payload (checkout.session.completed,
 *   customer.subscription.created / updated / deleted) and updates the
 *   user's `isSubscribed` flag / school plan status server-side.
 *   Until then, redirect → success page is the smooth UX, but entitlement
 *   activation requires manual/admin confirmation.
 */
export const stripePaymentLinks = {
  createSchoolSubscription: "https://buy.stripe.com/6oU14oe0BbUw1Ot1GVew800",
  stargateControlTowerLifetime: "https://buy.stripe.com/fZubJ28Gh1fSakZ71few801",
  adultBibleStudyWeekly: "https://buy.stripe.com/00waEYbStf6Idxb71few802",
  joinSchoolSubscription: "https://buy.stripe.com/3cIaEY1dPf6I9gVgBPew803",
} as const;

export type StripePaymentLinkKey = keyof typeof stripePaymentLinks;

export function openStripePaymentLink(key: StripePaymentLinkKey): void {
  const url = stripePaymentLinks[key];
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Whether Stripe Payment Link buttons should be shown in the current runtime.
 *
 * Returns FALSE inside the Android Capacitor wrapper — Google Play Store
 * policy prohibits third-party payment processing for digital goods, so
 * the Android app must use Google Play Billing exclusively. Returns TRUE
 * only on a clearly-confirmed web browser context.
 *
 * FAIL-CLOSED: any native indicator (Capacitor object present, Cordova,
 * `capacitor:` scheme, Android WebView UA, missing window/document) hides
 * Stripe. We only show Stripe when ALL signals say "real browser." This
 * is intentional — a runtime gate is the only Stripe suppressor on
 * Android, so a missed detection becomes a Play Store policy violation.
 *
 * Always gate Stripe button rendering on this helper:
 *   {showStripeButtons() && <Button onClick={...}>Subscribe via Stripe</Button>}
 */
export function showStripeButtons(): boolean {
  return showWebPaymentButtons();
}
